import React, { useCallback, useEffect, useState } from "react";
import { BackButton, Button, Card, Chip, Input, SectionTitle, EmptyState } from "./ui";
import { api } from "./api";

/* -------------------------------------------------------------------------- */
/* 🚑 Emergencia veterinaria — encontrar clínicas cercanas / 24h / emergencias */
/* -------------------------------------------------------------------------- */

const DAY_LABELS = {
  monday: "Lun", tuesday: "Mar", wednesday: "Mié", thursday: "Jue",
  friday: "Vie", saturday: "Sáb", sunday: "Dom",
};
const DAY_ORDER = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

function directionsUrl(v) {
  if (v.lat != null && v.lng != null) {
    return isIOS()
      ? `http://maps.apple.com/?daddr=${v.lat},${v.lng}`
      : `https://www.google.com/maps/dir/?api=1&destination=${v.lat},${v.lng}`;
  }
  if (v.address) {
    const q = encodeURIComponent(`${v.name} ${v.address}`);
    return `https://www.google.com/maps/search/?api=1&query=${q}`;
  }
  return null;
}

function OpenBadge({ state }) {
  if (state === "open")
    return <span data-testid="vet-open-badge"><Chip tone="mint">🟢 Abierto ahora</Chip></span>;
  if (state === "closed")
    return <span data-testid="vet-open-badge"><Chip tone="coral">🔴 Cerrado</Chip></span>;
  return <span data-testid="vet-open-badge"><Chip tone="gray">🕐 Horario no disponible</Chip></span>;
}

function ClinicActions({ v, size = "" }) {
  const dir = directionsUrl(v);
  // 🚑 Llamar ahora: solo si atiende emergencias, hay teléfono y está abierta (o 24h).
  const showEmergencyCta =
    v.emergency_service && v.phone && (v.open_state === "open" || v.open_24_hours);
  return (
    <>
      {showEmergencyCta ? (
        <a href={`tel:${v.phone}`} data-testid={`vet-emergency-call-${v.id}`}
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-2xl bg-[#E11D48] px-4 py-3.5 text-base font-black text-white shadow-[0_10px_24px_-10px_rgba(225,29,72,0.7)] active:translate-y-[1px]">
          🚑 Llamar ahora
        </a>
      ) : null}
      <div className="mt-3 flex flex-wrap gap-2">
        {v.phone ? (
          <a href={`tel:${v.phone}`} data-testid={`vet-call-${v.id}`}
            className={`inline-flex items-center gap-1.5 rounded-full bg-[#0B2D4D] px-4 py-2 text-sm font-bold text-white active:translate-y-[1px] ${size}`}>
            📞 Llamar
          </a>
        ) : null}
        {v.whatsapp ? (
          <a href={`https://wa.me/${v.whatsapp}`} target="_blank" rel="noreferrer" data-testid={`vet-whatsapp-${v.id}`}
            className={`inline-flex items-center gap-1.5 rounded-full bg-[#25D366] px-4 py-2 text-sm font-bold text-white active:translate-y-[1px] ${size}`}>
            💬 WhatsApp
          </a>
        ) : null}
        {dir ? (
          <a href={dir} target="_blank" rel="noreferrer" data-testid={`vet-directions-${v.id}`}
            className={`inline-flex items-center gap-1.5 rounded-full bg-[#FF5C5C] px-4 py-2 text-sm font-bold text-white active:translate-y-[1px] ${size}`}>
            📍 Cómo llegar
          </a>
        ) : null}
      </div>
    </>
  );
}

function ClinicCard({ v, onOpen }) {
  return (
    <Card className="p-4" data-testid={`vet-card-${v.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-display text-lg font-black text-[#0B2D4D]">{v.name}</p>
            {v.is_pawcita_partner ? (
              <span data-testid={`vet-partner-${v.id}`}><Chip tone="yellow">🐾 Aliado PawCita</Chip></span>
            ) : null}
          </div>
          {v.address ? <p className="mt-0.5 text-sm text-slate-500">{v.address}</p> : null}
        </div>
        {v.distance_km != null ? (
          <span className="flex-shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-[#0B2D4D]"
            data-testid={`vet-distance-${v.id}`}>
            {v.distance_km < 1 ? `${Math.round(v.distance_km * 1000)} m` : `${v.distance_km.toFixed(1)} km`}
          </span>
        ) : null}
      </div>

      <div className="mt-2 flex flex-wrap gap-1.5">
        <OpenBadge state={v.open_state} />
        {v.open_24_hours ? <Chip tone="navy">🕐 24 horas</Chip> : null}
        {v.emergency_service ? <Chip tone="coral">🚑 Emergencias</Chip> : null}
        {v.is_verified ? <Chip tone="mint">✓ Verificado</Chip> : null}
      </div>

      {v.services && v.services.length ? (
        <p className="mt-2 text-sm text-slate-600">{v.services.slice(0, 4).join(" · ")}</p>
      ) : null}

      <ClinicActions v={v} />
      <button onClick={() => onOpen(v)} data-testid={`vet-details-${v.id}`}
        className="mt-3 text-sm font-bold text-[#0B2D4D] underline underline-offset-4">
        Ver detalles ›
      </button>
    </Card>
  );
}

function ClinicDetail({ v, onBack }) {
  return (
    <div className="mx-auto max-w-3xl px-4 pb-28 pt-4 sm:px-6" data-testid="vet-detail-screen">
      <div className="mb-4"><BackButton onClick={onBack} label="Volver" testId="vet-detail-back" /></div>
      <Card className="p-5">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="font-display text-2xl font-black text-[#0B2D4D]">{v.name}</h2>
          {v.is_pawcita_partner ? <Chip tone="yellow">🐾 Aliado PawCita</Chip> : null}
          {v.is_verified ? <Chip tone="mint">✓ Verificado</Chip> : null}
        </div>
        {v.address ? <p className="mt-1 text-sm text-slate-500">{v.address}</p> : null}

        <div className="mt-3 flex flex-wrap gap-1.5">
          <OpenBadge state={v.open_state} />
          {v.open_24_hours ? <Chip tone="navy">🕐 24 horas</Chip> : null}
          {v.emergency_service ? <Chip tone="coral">🚑 Emergencias</Chip> : null}
        </div>

        <ClinicActions v={v} />

        {v.opening_hours ? (
          <div className="mt-5" data-testid="vet-hours">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-[#FF5C5C]">Horarios</p>
            <div className="mt-2 space-y-1">
              {DAY_ORDER.map((d) => {
                const h = v.opening_hours[d];
                if (!h) return null;
                return (
                  <div key={d} className="flex justify-between text-sm">
                    <span className="text-slate-500">{DAY_LABELS[d]}</span>
                    <span className="font-semibold text-[#0B2D4D]">
                      {h.closed ? "Cerrado" : h.open && h.close ? `${h.open} – ${h.close}` : "No disponible"}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ) : v.open_24_hours ? null : (
          <p className="mt-5 text-sm text-slate-500">Horario no disponible.</p>
        )}

        {v.services && v.services.length ? (
          <div className="mt-5" data-testid="vet-services">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-[#FF5C5C]">Servicios</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {v.services.map((s, i) => <Chip key={i} tone="gray">{s}</Chip>)}
            </div>
          </div>
        ) : null}

        {v.website ? (
          <a href={v.website} target="_blank" rel="noreferrer" data-testid="vet-website"
            className="mt-5 inline-block text-sm font-bold text-[#0B2D4D] underline underline-offset-4">
            🌐 Sitio web
          </a>
        ) : null}
      </Card>
    </div>
  );
}

export default function VetEmergency({ onBack, showToast }) {
  const [list, setList] = useState(null);
  const [err, setErr] = useState("");
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ near: false, open_now: false, open_24_hours: false, emergency_service: false });
  const [coords, setCoords] = useState(null);
  const [locating, setLocating] = useState(false);

  const load = useCallback(async () => {
    setErr("");
    try {
      const params = {
        search: search.trim(),
        open_now: filters.open_now,
        open_24_hours: filters.open_24_hours,
        emergency_service: filters.emergency_service,
      };
      if (filters.near && coords) { params.lat = coords.lat; params.lng = coords.lng; params.radius_km = 50; }
      const data = await api.veterinaries.list(params);
      setList(data);
    } catch (e) {
      setErr(e.message || "No se pudo cargar el directorio.");
      setList([]);
    }
  }, [search, filters, coords]);

  useEffect(() => { load(); }, [load]);

  const requestLocation = () => {
    // Only on explicit user gesture; deny keeps the directory usable.
    if (filters.near) { setFilters((f) => ({ ...f, near: false })); return; }
    if (!navigator.geolocation) { showToast && showToast("Tu dispositivo no permite ubicación.", "navy"); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setFilters((f) => ({ ...f, near: true }));
        setLocating(false);
      },
      () => {
        setLocating(false);
        showToast && showToast("Sin ubicación: te mostramos el directorio completo.", "navy");
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 },
    );
  };

  const toggle = (k) => setFilters((f) => ({ ...f, [k]: !f[k] }));

  if (selected) return <ClinicDetail v={selected} onBack={() => setSelected(null)} />;

  const chip = (active) =>
    `inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold transition active:translate-y-[1px] ${
      active ? "bg-[#0B2D4D] text-white" : "bg-white text-[#0B2D4D] ring-1 ring-slate-200"
    }`;

  return (
    <div className="mx-auto max-w-3xl px-4 pb-28 pt-4 sm:px-6" data-testid="vet-emergency-screen">
      <div className="mb-4"><BackButton onClick={onBack} testId="vet-back-btn" /></div>
      <SectionTitle eyebrow="Seguridad">🚑 Emergencia veterinaria</SectionTitle>
      <p className="mt-1 text-sm text-slate-500">Encuentra atención veterinaria cuando más la necesitas.</p>

      <div className="mt-4 flex flex-wrap gap-2">
        <button className={chip(filters.near)} onClick={requestLocation} data-testid="vet-filter-near">
          📍 {locating ? "Ubicando…" : "Cerca de mí"}
        </button>
        <button className={chip(filters.open_now)} onClick={() => toggle("open_now")} data-testid="vet-filter-open">
          🟢 Abierto ahora
        </button>
        <button className={chip(filters.open_24_hours)} onClick={() => toggle("open_24_hours")} data-testid="vet-filter-24h">
          🕐 24 horas
        </button>
        <button className={chip(filters.emergency_service)} onClick={() => toggle("emergency_service")} data-testid="vet-filter-emergency">
          🚑 Emergencias
        </button>
      </div>

      <div className="mt-3">
        <Input placeholder="Buscar por nombre o zona…" value={search}
          onChange={(e) => setSearch(e.target.value)} testId="vet-search-input" />
      </div>

      {err ? <p className="mt-4 text-sm text-red-500" data-testid="vet-error">{err}</p> : null}

      {list == null ? (
        <p className="mt-6 text-sm text-slate-400" data-testid="vet-loading">Cargando clínicas…</p>
      ) : list.length === 0 ? (
        <div className="mt-6">
          <EmptyState emoji="🚑" title="Sin resultados"
            text="Ajusta los filtros o borra la búsqueda para ver más clínicas." />
        </div>
      ) : (
        <div className="mt-5 space-y-3" data-testid="vet-list">
          {list.map((v) => <ClinicCard key={v.id} v={v} onOpen={setSelected} />)}
        </div>
      )}
    </div>
  );
}
